import React, {useState, useRef, FC} from 'react';
import { ChevronDown, Search, X, AlertCircle } from 'lucide-react';
import countries from "../utils/countries.ts";

interface PhoneNumberInputProps {
    setIsPhoneValid: any,
    autoSet?: boolean,
    onValidPhone: (phoneNumber: string) => void,
    required: boolean
}

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
                              setIsPhoneValid,
                              onValidPhone,
    autoSet= false,
                              required = false
                          }) => {
    const [selectedCountry, setSelectedCountry] = useState(countries[0]);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [touched, setTouched] = useState(false);
    const [isValid, setIsValid] = useState(!required);
    const [errorMessage, setErrorMessage] = useState('');
    const inputRef = useRef(null);
    const searchInputRef = useRef(null);

    /*useEffect(() => {
        handleBlur()
    }, [phoneNumber]);*/

   /* useEffect(() => {
        if(defaultValue){
            setPhoneNumber(defaultValue);
        }
    }, []);*/

    const validatePhoneNumber = (number: string | any[], country: { code: string; flag: string; dialCode: string; format: string; placeholder: string; length: number; pattern: RegExp; name: string; mask: string; example: string; } | { code: string; flag: string; dialCode: string; format: string; placeholder: string; length: number; pattern: RegExp; name: string; mask?: undefined; example?: undefined; }) => {
        // Si non requis et vide, considéré comme valide
        if (!required && !number) return { valid: true, message: '' };

        // Vérification de base
        if (!number) {
            return { valid: false, message: 'Le numéro de téléphone est requis' };
        }

        // Vérification de la longueur
        if (number.length !== country.length) {
            return {
                valid: false,
                message: `Le numéro doit contenir ${country.length} chiffres`
            };
        }

        // Vérification du format
        if (!country.pattern.test(number as string)) {
            const prefixMessages = {
                'TG': 'Le numéro doit commencer par 90-99, 70-79, ou 22-24',
                'FR': 'Le numéro doit commencer par 6 ou 7'
            };
            return {
                valid: false,
                // @ts-ignore
                message: prefixMessages[country.code] || 'Format de numéro invalide'
            };
        }

        return { valid: true, message: '' };
    };

    const handlePhoneChange = (e:any) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        const formatted = rawValue.slice(0, selectedCountry.length);
        setPhoneNumber(formatted);
        if (autoSet) onValidPhone?.(`${selectedCountry.dialCode}${formatted}`);
    };

    const handleBlur = () => {
        setTouched(true);
        const validation = validatePhoneNumber(phoneNumber, selectedCountry);
        handleIsHandle(validation.valid);
        setErrorMessage(validation.message);

        // Appel du callback si le numéro est valide
        if (validation.valid) {
            onValidPhone?.(`${selectedCountry.dialCode}${phoneNumber}`);
        }
    };

    const handleCountrySelect = (country: React.SetStateAction<{ code: string; flag: string; dialCode: string; format: string; placeholder: string; length: number; pattern: RegExp; name: string; mask: string; example: string; } | { code: string; flag: string; dialCode: string; format: string; placeholder: string; length: number; pattern: RegExp; name: string; mask?: undefined; example?: undefined; }>) => {
        setSelectedCountry(country);
        setIsModalOpen(false);
        setSearchTerm('');
        setPhoneNumber('');
        setTouched(false);
        handleIsHandle(!required);
        setErrorMessage('');
    };

    const handleIsHandle = (value: boolean) => {
        setIsValid(value);
        setIsPhoneValid(value);
    }

    return (
        <div className="w-full max-w-md space-y-2">
            <div className="relative">
                <div className={`flex items-center border rounded-xl overflow-hidden 
                      ${touched && !isValid ? 'border-red-500' : 'border-gray-300'}
                      ${touched && isValid ? 'border-green-500' : ''}`}>
                    <button
                        type="button"
                        onClick={() => {
                            // @ts-ignore
                            if(!countries.length > 1 ) {
                                setIsModalOpen(true)
                            }
                        }}
                        className="flex items-center gap-2 pl-4 pr-2 py-3 hover:bg-gray-50"
                    >
                        <img src={selectedCountry?.flag} alt={selectedCountry?.code}/>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>

                    <div className="flex-1 flex items-center">
                        <span className="text-gray-500 pl-2">{selectedCountry.dialCode}</span>
                        <input
                            ref={inputRef}
                            type="tel"
                            value={phoneNumber}
                            onChange={handlePhoneChange}
                            onBlur={handleBlur}
                            placeholder={selectedCountry.placeholder}
                            className="flex-1 px-2 py-3 focus:outline-none text-gray-900 placeholder-gray-400"
                        />
                    </div>
                </div>

                {touched && !isValid && (
                    <div className="mt-1 flex items-center gap-2 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        {errorMessage}
                    </div>
                )}
            </div>

            {/* Modal de sélection de pays */}
            {isModalOpen && (
                <div className="fixed inset-0 p-4 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col">
                        {/* Contenu de la modal (identique à l'implémentation précédente) */}
                        <div className="p-4 border-b flex items-center">
                            <div className="relative flex-1 mr-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Rechercher un pays"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="overflow-y-auto">
                            {[{
                                code: 'TG',
                                flag: '/FLAGS/TG.png',
                                dialCode: '+228',
                                format: 'XXXXXXXX',
                                placeholder: '90123456',
                                length: 8,
                                pattern: /^(9[0-9]|7[0-9]|2[2-4])\d{6}$/,
                                name: 'Togo'
                            }].filter(country =>
                                country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                country.code.toLowerCase().includes(searchTerm.toLowerCase())
                            ).map((country) => (
                                <button
                                    key={country.code}
                                    onClick={() => handleCountrySelect(country)}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center"
                                >
                                    <img className="mr-4" src={country?.flag} alt={country?.code}/>
                                    <div className="flex-1">
                                    <div className="font-medium">{country.name}</div>
                                        <div className="text-sm text-gray-500">{country.dialCode}</div>
                                    </div>
                                    <span className="text-gray-500 text-sm">{country.code}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhoneNumberInput;
